export const ALLOWED_STYLE_TOKENS = [
  '--bg-primary',
  '--bg-raised',
  '--bg-subtle',
  '--bg-gradient',
  '--bg-noise',
  '--glyph-primary',
  '--glyph-secondary',
  '--accent-primary',
  '--outline-default',
  '--glyph-success',
  '--glyph-critical',
  '--radius-md',
  '--radius-lg',
  '--radius-full',
  '--font-family-title',
  '--font-family-body',
  '--font-size-body',
] as const;

export type AllowedStyleToken = (typeof ALLOWED_STYLE_TOKENS)[number];

/** Style tokens used by the static quiz block in newsletter email HTML. */
export const EMAIL_QUIZ_STYLE_TOKENS = [
  '--accent-primary',
  '--bg-subtle',
  '--bg-raised',
  '--outline-default',
  '--glyph-primary',
  '--glyph-secondary',
  '--radius-md',
  '--font-family-title',
  '--font-family-body',
  '--font-size-body',
] as const satisfies readonly AllowedStyleToken[];

export type EmailQuizStyleToken = (typeof EMAIL_QUIZ_STYLE_TOKENS)[number];

export function pickEmailQuizStyleTokens(
  tokens: Partial<Record<AllowedStyleToken, string>>,
): Partial<Record<EmailQuizStyleToken, string>> {
  const picked: Partial<Record<EmailQuizStyleToken, string>> = {};
  for (const token of EMAIL_QUIZ_STYLE_TOKENS) {
    const value = tokens[token];
    if (value) {
      picked[token] = value;
    }
  }
  return picked;
}

export const DEFAULT_ATRIVIAL_TOKENS: Record<AllowedStyleToken, string> = {
  '--bg-primary': 'oklch(99% 0.003 240)',
  '--bg-raised': 'oklch(100% 0 0)',
  '--bg-subtle': 'oklch(98% 0 240)',
  '--bg-gradient':
    'linear-gradient(to bottom, transparent 0%, oklch(96% 0.001 244) 100%)',
  '--bg-noise': '',
  '--glyph-primary': 'oklch(30% 0 240)',
  '--glyph-secondary': 'oklch(38% 0 240)',
  '--accent-primary': 'oklch(25% 0 240)',
  '--outline-default': 'oklch(92% 0 240)',
  '--glyph-success': 'oklch(51% 0.16 161)',
  '--glyph-critical': 'oklch(58% 0.22 26)',
  '--radius-md': '8px',
  '--radius-lg': '8px',
  '--radius-full': '9999px',
  '--font-family-title': "'Libre Franklin', helvetica, system-ui, sans-serif",
  '--font-family-body': "'Lato', helvetica, system-ui, sans-serif",
  '--font-size-body': '16px',
};

const allowedTokenSet = new Set<string>(ALLOWED_STYLE_TOKENS);

/**
 * Merges token layers with later layers taking precedence.
 * base < override (left to right)
 */
export function mergeTokenLayers(
  ...layers: (Partial<Record<AllowedStyleToken, string>> | undefined | null)[]
): Partial<Record<AllowedStyleToken, string>> {
  const result: Partial<Record<AllowedStyleToken, string>> = {};
  for (const layer of layers) {
    if (!layer) continue;
    for (const [key, value] of Object.entries(layer)) {
      if (allowedTokenSet.has(key) && value) {
        result[key as AllowedStyleToken] = value;
      }
    }
  }
  return result;
}
